import React, { useCallback, useState } from "react";

import { Autocomplete, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { CreateDealContent } from "components/Prompts/CreateDealContent";
import { useCanCreateDeal, usePrompt } from "hooks";
import { useTaskDealsSelect } from "store";
import { DealBase, Nullable } from "store/types";

interface Props {
  onSubmit: Nullable<(id: number, title: string) => void>;
  onCancel: () => void;
}

export const SelectDeal: React.FC<Props> = ({ onSubmit, onCancel }) => {
  const deals = useTaskDealsSelect();
  const openPrompt = usePrompt();
  const canCreateDeal = useCanCreateDeal();

  const [value, setValue] = useState<DealBase | null>(null);

  const submitWrap = useCallback(() => {
    if (value && onSubmit) {
      onSubmit(value.id, value.title);
      setValue(null);
      onCancel();
    }
  }, [onSubmit, value]);

  const handleCreateDeal = async (e: React.MouseEvent) => {
    e.preventDefault();
    const data = await openPrompt({ Component: CreateDealContent });
    if (!data) return;
    setValue({ id: data.dealId, title: data.dealTitle });
  };

  return (
    <Modal open={!!onSubmit}>
      <Box sx={{ ...style }}>
        <Box>
          <Typography sx={{ fontSize: "16px", color: "#0a3142" }}>Deal Select</Typography>
          <Stack gap="10px" mb="20px">
            <Stack gap="10px" sx={{ backgroundColor: "#dae0e2", height: "1px", marginBottom: "14px" }} />
            <Typography>Please select a deal to add data</Typography>
            <Autocomplete
              value={value}
              onChange={(_, option) => setValue(option)}
              options={deals}
              renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: DealBase) => (
                <li {...props} key={option.id}>
                  {option.title}
                </li>
              )}
              PaperComponent={({ children }) => (
                <Paper>
                  {children}
                  {canCreateDeal && (
                    <Button
                      onMouseDown={handleCreateDeal}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        height: "50px",
                        backgroundColor: "#fff",
                        boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
                        borderRadius: "0 0 10px 10px"
                      }}
                      fullWidth
                    >
                      Add Deal
                    </Button>
                  )}
                </Paper>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  dir="auto"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ ".MuiInputBase-root": { borderRadius: "8px", width: "280px" } }}
                  label="Deal"
                  fullWidth
                />
              )}
              getOptionLabel={(option) => option.title}
            />
          </Stack>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={onCancel} sx={{ marginRight: "20px" }} variant="outlined">
              Close
            </Button>
            <Button onClick={submitWrap} disabled={!value} variant="contained">
              Continue
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "8px",
  backgroundColor: "#fff",
  padding: "30px"
};
